import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import { triggerMoved } from "./triggerMoved";

/** Rough panel height budget used only to decide whether to flip the panel
 * above the trigger when there isn't room below (mirrors `SelectMenu`). */
const PANEL_MAX_H = 280;

/** Default height cap (20rem) before the panel scrolls. */
const DEFAULT_MAX_HEIGHT = 320;

/** Gap kept between the panel and the viewport edge. */
const VIEWPORT_MARGIN = 8;

type PanelPos = {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  maxHeight: number;
};

/**
 * A lightweight dropdown menu primitive — no external dependency. Owns its own
 * open state and renders a trigger plus a popover panel that:
 *   • closes on outside pointer-down and on `Escape`,
 *   • moves roving focus across its items with ArrowUp/ArrowDown/Home/End,
 *   • focuses the first item when opened via keyboard,
 *   • returns focus to the trigger on close.
 *
 * The trigger is supplied as a render function so callers own the button
 * element (avatar, "+ New" pill, …) while still getting the wiring they need:
 * `{ open, ref, toggleProps }`. Spread `toggleProps` onto the button.
 *
 * Reused by the account menu, the language/theme choices within it, and the
 * New-ticket queue picker.
 */
type MenuContextValue = { close: () => void };
const MenuContext = createContext<MenuContextValue | null>(null);

type TriggerArgs = {
  open: boolean;
  ref: React.RefObject<HTMLButtonElement | null>;
  toggleProps: {
    "aria-haspopup": "menu";
    "aria-expanded": boolean;
    onClick: () => void;
    onKeyDown: (e: React.KeyboardEvent) => void;
  };
};

export function Menu({
  trigger,
  children,
  align = "right",
  panelClassName,
  panelTestId,
  maxHeight = DEFAULT_MAX_HEIGHT,
}: {
  trigger: (args: TriggerArgs) => ReactNode;
  children: ReactNode;
  align?: "left" | "right";
  panelClassName?: string;
  panelTestId?: string;
  /** Height cap before the panel scrolls; `null` grows up to the viewport
   * (short fixed menus like the account menu that must show every entry).
   * Either way the panel never extends past the viewport edge. */
  maxHeight?: number | null;
}) {
  const [open, setOpen] = useState(false);
  const [autoFocus, setAutoFocus] = useState(false);
  const [pos, setPos] = useState<PanelPos | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const openRectRef = useRef<DOMRect | null>(null);
  const menuId = useId();

  const close = useCallback(() => setOpen(false), []);

  // The panel is portaled to `document.body` (see below) so it is never clipped
  // by an `overflow` ancestor such as the admin `DataTable` scroll container —
  // previously the row "Aktionen" menu was swallowed for rows near the bottom.
  // Derive its fixed position from the trigger rect on open, flipping above when
  // there isn't room below.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    openRectRef.current = rect;
    const spaceBelow = window.innerHeight - rect.bottom;
    const flip = spaceBelow < PANEL_MAX_H && rect.top > spaceBelow;
    const room = (flip ? rect.top : spaceBelow) - 6 - VIEWPORT_MARGIN;
    setPos({
      maxHeight: maxHeight == null ? room : Math.min(maxHeight, room),
      top: flip ? undefined : rect.bottom + 6,
      bottom: flip ? window.innerHeight - rect.top + 6 : undefined,
      left: align === "right" ? undefined : rect.left,
      right: align === "right" ? window.innerWidth - rect.right : undefined,
    });
  }, [open, align, maxHeight]);

  // Anchored to the trigger, the panel can stick out past the viewport edge on a phone. Its
  // width is only known once rendered: shift it back in.
  useLayoutEffect(() => {
    const width = panelRef.current?.getBoundingClientRect().width;
    if (!pos || !width) return;
    const limit = Math.max(VIEWPORT_MARGIN, window.innerWidth - VIEWPORT_MARGIN - width);
    if (pos.right !== undefined && pos.right > limit) setPos({ ...pos, right: limit });
    if (pos.left !== undefined && pos.left > limit) setPos({ ...pos, left: limit });
  }, [pos]);

  // Outside pointer-down + Escape close, and initial focus into the panel when
  // opened by keyboard. Focus returns to the trigger on close.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      // A `SelectMenu` panel (e.g. the language picker nested in the account
      // menu) is portal-rendered to `document.body`, so it sits outside this
      // panel's DOM subtree — without this check, clicking into it would
      // look like an outside click and close the surrounding Menu.
      if (target instanceof Element && target.closest("[data-portal-menu]")) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    // The panel is fixed-positioned from the trigger rect on open; page scroll
    // or resize would leave it stranded, so close rather than re-track — but
    // only once the trigger really moved (see `triggerMoved`).
    // Scrolling *inside* this panel (long menus) must not close it, and neither
    // may scrolling a nested portal listbox such as the account language
    // `SelectMenu` — those render into `document.body` under
    // `[data-portal-menu]`, outside this panel's DOM subtree.
    const onScroll = (e: Event) => {
      const target = e.target;
      if (target instanceof Node && panelRef.current?.contains(target)) return;
      const el =
        target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
      if (el?.closest("[data-portal-menu]")) return;
      if (!triggerMoved(triggerRef.current, openRectRef.current)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  // The panel only exists once `pos` is set (layout effect after opening), so wait for it.
  useEffect(() => {
    if (open && autoFocus && pos) {
      itemAt(panelRef.current, 0)?.focus();
      setAutoFocus(false);
    }
  }, [open, autoFocus, pos]);

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setAutoFocus(true);
      setOpen(true);
    }
  };

  const onPanelKeyDown = (e: React.KeyboardEvent) => {
    const items = itemList(panelRef.current);
    if (items.length === 0) return;
    const current = document.activeElement as HTMLElement | null;
    const idx = current ? items.indexOf(current) : -1;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(idx + 1 + items.length) % items.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      {trigger({
        open,
        ref: triggerRef,
        toggleProps: {
          "aria-haspopup": "menu",
          "aria-expanded": open,
          onClick: () => setOpen((o) => !o),
          onKeyDown: onTriggerKeyDown,
        },
      })}
      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            id={menuId}
            data-testid={panelTestId}
            data-portal-menu
            onKeyDown={onPanelKeyDown}
            style={{ position: "fixed", maxWidth: `calc(100vw - ${2 * VIEWPORT_MARGIN}px)`, ...pos }}
            className={cn(
              "z-50 min-w-[13rem] overflow-y-auto rounded-xl border border-hairline bg-surface p-1 shadow-xl animate-route-in",
              panelClassName,
            )}
          >
            <MenuContext.Provider value={{ close }}>{children}</MenuContext.Provider>
          </div>,
          document.body,
        )}
    </div>
  );
}

function itemList(panel: HTMLElement | null): HTMLElement[] {
  if (!panel) return [];
  return Array.from(panel.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])'));
}

function itemAt(panel: HTMLElement | null, index: number): HTMLElement | undefined {
  return itemList(panel)[index];
}

/** A single actionable menu row. Closes the menu after firing `onSelect`
 * (unless `keepOpen`). Renders as a button; pass `selected` to show a check.
 * `highlight` marks an attention entry (e.g. Admin area) with accent fill. */
export function MenuItem({
  onSelect,
  children,
  icon,
  selected,
  keepOpen,
  danger,
  highlight,
  testId,
  disabled,
  softDisabled,
  title,
  description,
}: {
  onSelect?: () => void;
  children: ReactNode;
  icon?: ReactNode;
  selected?: boolean;
  keepOpen?: boolean;
  danger?: boolean;
  highlight?: boolean;
  testId?: string;
  /** Really disabled: skipped by arrow-key navigation. */
  disabled?: boolean;
  /** Looks and answers disabled (`aria-disabled`) but stays focusable, so its `title` can explain why. */
  softDisabled?: boolean;
  title?: string;
  /** Announced with the item (`aria-describedby`), e.g. why a soft-disabled item does nothing:
   * a `title` alone is not read out on keyboard focus. */
  description?: string;
}) {
  const ctx = useContext(MenuContext);
  const descId = useId();
  const inactive = disabled === true || softDisabled === true;
  return (
    <>
      <button
        type="button"
        role="menuitem"
        tabIndex={-1}
        data-testid={testId}
        disabled={disabled}
        aria-disabled={softDisabled ? true : undefined}
        aria-describedby={description ? descId : undefined}
        title={title}
        onClick={() => {
          if (softDisabled) return;
          onSelect?.();
          if (!keepOpen) ctx?.close();
        }}
        className={cn(
          "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] transition-colors duration-100 focus:outline-none",
          inactive
            ? "cursor-not-allowed text-ink/90 opacity-50"
            : highlight
            ? "bg-accent/15 font-semibold text-accent hover:bg-accent/25 focus-visible:bg-accent/25"
            : danger
              ? "text-danger hover:bg-danger/10 focus-visible:bg-danger/10"
              : "text-ink/90 hover:bg-surface-subtle focus-visible:bg-surface-subtle",
        )}
      >
        {icon != null && (
          <span
            className={cn(
              "flex w-4 shrink-0 justify-center text-[15px]",
              highlight ? "text-accent" : "text-muted",
            )}
            aria-hidden
          >
            {icon}
          </span>
        )}
        <span className="min-w-0 flex-1 truncate">{children}</span>
        {selected != null && (
          <span className="flex w-4 shrink-0 justify-center text-accent" aria-hidden>
            {selected ? <CheckMark /> : null}
          </span>
        )}
      </button>
      {description && (
        <span id={descId} hidden>
          {description}
        </span>
      )}
    </>
  );
}

/** Shared with `SelectMenu` (the portal-based listbox), which shows the same
 * "current value" tick next to its selected option. */
export function CheckMark() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      width="0.85em"
      height="0.85em"
      aria-hidden="true"
    >
      <path d="m5 12.5 4.5 4.5L19 6.5" />
    </svg>
  );
}

/** Non-interactive section label (e.g. the "Sprache" / "Theme" group headers). */
export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <p className="px-2.5 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted">
      {children}
    </p>
  );
}

/** A header block for the account identity (name + login). */
export function MenuHeader({ children }: { children: ReactNode }) {
  return <div className="border-b border-hairline px-2.5 py-2">{children}</div>;
}

export function MenuSeparator() {
  return <div role="separator" className="my-1 border-t border-hairline" />;
}
